import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import { Download, Trash2, RefreshCw, UploadCloud, FileText, Plus, X, Database, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

function UploadModal({ onClose, onUploaded }) {
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && f.name.toLowerCase().endsWith('.sav')) {
      setUploadFile(f);
      setError(null);
    } else {
      setError('Only .sav files are accepted.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFile) { setError('Please select a .sav file.'); return; }
    const formData = new FormData();
    formData.append('name', uploadFile.name.replace(/\.sav$/i, ''));
    formData.append('description', uploadDescription);
    formData.append('file', uploadFile);
    setIsUploading(true);
    setError(null);
    try {
      await api.post('bcm_norms/spss-files/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Check the file format and your permissions.');
    } finally {
      setIsUploading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <motion.div
        className="absolute inset-0 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        initial={{ scale: 0.95, opacity: 0, y: 8 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 8 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center">
              <UploadCloud size={18} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Upload SPSS File</h2>
              <p className="text-xs text-gray-400">Accepted format: .sav</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={17} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={[
              'border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all select-none',
              dragging ? 'border-indigo-400 bg-indigo-50' :
              uploadFile ? 'border-indigo-300 bg-indigo-50/50' :
              'border-gray-200 hover:border-indigo-300 hover:bg-gray-50',
            ].join(' ')}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".sav"
              className="hidden"
              onChange={(e) => { setUploadFile(e.target.files[0]); setError(null); }}
            />
            {uploadFile ? (
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <FileText size={18} className="text-indigo-600" />
                </div>
                <p className="text-sm font-medium text-indigo-700 truncate max-w-xs">{uploadFile.name}</p>
                <p className="text-xs text-gray-400">{(uploadFile.size / 1024 / 1024).toFixed(2)} MB · click to change</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <UploadCloud size={18} className="text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600">Drop your file here, or <span className="text-indigo-600">browse</span></p>
                <p className="text-xs text-gray-400">SPSS .sav format only</p>
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Description <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Notes about this dataset…"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent resize-none text-gray-700 placeholder-gray-300"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3.5 py-2.5">
              <AlertCircle size={15} className="mt-0.5 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <RefreshCw size={14} className="animate-spin" /> Uploading…
                </>
              ) : (
                <>
                  <UploadCloud size={14} /> Upload File
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </div>,
    document.body
  );
}

export default function SpssFileManager() {
  const [files, setFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const replaceFileInputRef = useRef(null);

  const fetchFiles = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('bcm_norms/spss-files/');
      setFiles(response.data || []);
    } catch {
      setError('Failed to load files. Please refresh.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchFiles(); }, []); // eslint-disable-line

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file? This action cannot be undone.')) return;
    setIsLoading(true);
    setError(null);
    try {
      await api.delete(`bcm_norms/spss-files/${fileId}/`);
      fetchFiles();
    } catch {
      setError('Failed to delete the file.');
      setIsLoading(false);
    }
  };

  const triggerReplace = (fileId) => {
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.dataset.fileId = fileId;
      replaceFileInputRef.current.click();
    }
  };

  const handleReplaceFile = async (e) => {
    const newFile = e.target.files[0];
    const fileId = e.target.dataset.fileId;
    if (!newFile || !fileId) return;
    const formData = new FormData();
    formData.append('file', newFile);
    formData.append('name', newFile.name.replace(/\.sav$/i, ''));
    setIsLoading(true);
    setError(null);
    try {
      await api.patch(`bcm_norms/spss-files/${fileId}/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      fetchFiles();
    } catch {
      setError('Replace failed.');
      setIsLoading(false);
    } finally {
      if (e.target) e.target.value = null;
    }
  };

  const handleDownload = async (fileId, filename) => {
    setError(null);
    try {
      const response = await api.get(`bcm_norms/spss-files/${fileId}/download/`);
      const link = document.createElement('a');
      link.href = response.data.download_url;
      link.setAttribute('download', response.data.filename || filename || 'dataset');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err.response?.data?.error || 'Download failed.');
    }
  };

  const handleDownloadAllCombined = async () => {
    if (files.length === 0) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('bcm_norms/spss-files/download-all-combined/', { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'combined_all_data.sav';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=([^;]+)/);
        if (match) filename = match[1].replace(/UTF-8''/, '').replace(/['"]+/g, '');
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download combined SPSS file.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return iso; }
  };

  const formatSize = (bytes) => {
    if (!bytes) return null;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="min-h-screen bg-gray-50/80">
      <div className="max-w-5xl mx-auto px-5 py-8">

        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-sm">
              <Database size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 leading-tight">BCM Norms File Manager</h1>
              <p className="text-sm text-gray-500">
                {isLoading
                  ? 'Loading…'
                  : `${files.length} ${files.length === 1 ? 'file' : 'files'} stored`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleDownloadAllCombined}
              disabled={isLoading || files.length === 0}
              title="Download all as one combined SPSS file"
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <Download size={14} />
              Combined
            </button>
            <button
              onClick={fetchFiles}
              disabled={isLoading}
              title="Refresh"
              className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-500 transition-colors shadow-sm"
            >
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl shadow-sm transition-colors"
            >
              <Plus size={15} /> Upload
            </button>
          </div>
        </div>

        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-4 overflow-hidden"
            >
              <div className="flex items-center gap-2.5 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl">
                <AlertCircle size={15} className="flex-shrink-0" />
                <span className="flex-1">{error}</span>
                <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
                  <X size={14} />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* File list card */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          {files.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center py-24 text-gray-400">
              <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center mb-4">
                <Database size={28} className="opacity-30" />
              </div>
              <p className="text-base font-medium text-gray-500 mb-1">No files yet</p>
              <p className="text-sm text-gray-400 mb-5">Upload your first SPSS dataset to get started</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-colors shadow-sm"
              >
                <Plus size={15} /> Upload File
              </button>
            </div>
          ) : isLoading && files.length === 0 ? (
            <div className="flex items-center justify-center py-24 text-gray-400">
              <RefreshCw size={20} className="animate-spin mr-3 opacity-40" />
              <span className="text-sm">Loading files…</span>
            </div>
          ) : (
            <>
              {/* Table header */}
              <div className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-6 px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">File</span>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider hidden md:block">Uploaded</span>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider hidden lg:block text-right">Total Records</span>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider text-right">Actions</span>
              </div>

              {/* File rows */}
              <div className="divide-y divide-gray-50">
                <AnimatePresence>
                  {files.map((file) => (
                    <motion.div
                      key={file.id}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15 }}
                      className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-6 px-5 py-4 hover:bg-gray-50/60 transition-colors"
                    >
                      {/* File info */}
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                          <FileText size={16} className="text-indigo-500" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{file.filename || file.name}</p>
                          {file.description && (
                            <p className="text-xs text-gray-400 truncate max-w-xs mt-0.5">{file.description}</p>
                          )}
                        </div>
                      </div>

                      {/* Date */}
                      <span className="text-xs text-gray-400 hidden md:block whitespace-nowrap">
                        {formatDate(file.uploaded_at)}
                      </span>

                      {/* Total Records */}
                      <span className="text-xs font-semibold text-indigo-600 hidden lg:block whitespace-nowrap text-right">
                        {file.valid_n != null ? file.valid_n.toLocaleString('en-IN') : '—'}
                      </span>

                      {/* Actions */}
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => handleDownload(file.id, file.filename)}
                          title="Download"
                          className="p-2 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white transition-colors"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={() => triggerReplace(file.id)}
                          title="Replace with new file"
                          className="p-2 rounded-lg bg-amber-400 hover:bg-amber-500 text-white transition-colors"
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(file.id)}
                          title="Delete"
                          className="p-2 rounded-lg bg-red-500 hover:bg-red-600 text-white transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Upload Modal — rendered via portal to escape parent CSS transform/positioning */}
      <AnimatePresence>
        {isModalOpen && (
          <UploadModal
            onClose={() => setIsModalOpen(false)}
            onUploaded={fetchFiles}
          />
        )}
      </AnimatePresence>

      {/* Hidden replace input */}
      <input
        type="file"
        ref={replaceFileInputRef}
        className="hidden"
        accept=".sav"
        onChange={handleReplaceFile}
      />
    </div>
  );
}
